import React, { FunctionComponent } from 'react';
import { getCurrencySymbol, formatIntervalDisplay } from 'src/lib/utils';

interface InvoiceDiscount {
  percent_off: number | null;
  amount_off: number | null;
  duration: 'forever' | 'once' | 'repeating' | null;
}

interface SubscriptionPricing {
  /** The full price before any discount is applied */
  fullAmount: number;
  /** The billing interval count (e.g. 1, 3, 6) */
  intervalCount: number;
  /** The billing interval unit (e.g. 'month', 'week') */
  interval: string;
}

interface OwnProps {
  invoiceDiscount?: InvoiceDiscount | null;
  pricing: SubscriptionPricing;
  isNewZealandUser: boolean;
  hideThenPart?: boolean;
  currencySymbol?: string;
}

/**
 * Calculates the discounted amount based on the invoice discount object.
 * Returns the full amount if no applicable discount exists.
 */
export const calculateDiscountedAmount = (
  fullAmount: number,
  invoiceDiscount?: InvoiceDiscount | null
): {
  discountedAmount: number;
  hasOneOffDiscount: boolean;
  hasForeverDiscount: boolean;
} => {
  const { amount_off, percent_off, duration } = invoiceDiscount ?? {};

  let discountedAmount = fullAmount;
  if (duration === 'once' || duration === 'forever') {
    if (amount_off) {
      discountedAmount = fullAmount - amount_off / 100;
    } else if (percent_off) {
      discountedAmount = fullAmount * (1 - percent_off / 100);
    }
  }

  const hasOneOffDiscount =
    duration === 'once' && !!(percent_off || amount_off);

  const hasForeverDiscount =
    duration === 'forever' && !!(percent_off || amount_off);

  return { discountedAmount, hasOneOffDiscount, hasForeverDiscount };
};

/**
 * Shared component that renders the billing/pricing display for a subscription,
 * including one-off discount messaging when applicable.
 *
 * Used by both the "Current subscription" and "Upcoming subscription" cards.
 */
const SubscriptionDiscountDisplay: FunctionComponent<OwnProps> = ({
  invoiceDiscount,
  pricing,
  isNewZealandUser,
  hideThenPart,
  currencySymbol: currencySymbolProp,
}) => {
  const { fullAmount, intervalCount, interval } = pricing;
  const { discountedAmount, hasOneOffDiscount, hasForeverDiscount } =
    calculateDiscountedAmount(fullAmount, invoiceDiscount);

  const currencySymbol =
    currencySymbolProp || getCurrencySymbol(isNewZealandUser);
  const intervalDisplay = formatIntervalDisplay(intervalCount, interval);

  if (hasOneOffDiscount) {
    if (hideThenPart) {
      return (
        <>
          Billed {currencySymbol}
          {discountedAmount.toFixed(2)}
        </>
      );
    }
    return (
      <>
        Billed {currencySymbol}
        {discountedAmount.toFixed(2)} once and then {currencySymbol}
        {fullAmount.toFixed(2)} every {intervalDisplay} after
      </>
    );
  }

  if (hasForeverDiscount) {
    return (
      <>
        Billed {currencySymbol}
        {discountedAmount.toFixed(2)} every {intervalDisplay}
      </>
    );
  }

  return (
    <>
      Billed {currencySymbol}
      {fullAmount.toFixed(2)} every {intervalDisplay}
    </>
  );
};

export default SubscriptionDiscountDisplay;
